import {
  checkIsParent,
  isIndividual,
  checkTahtaAccess,
  checkIsRestricted,
  checkOkumanyaAccess,
  isAZRegion,
  checkPdevcAccess,
} from '@/utils/helpers/common';
import { useTranslation } from 'react-i18next';
import { getRegionConfig } from '@/utils/helpers/region';
import type { IMyInfo } from '@/services/user/types';
import type { IconName } from '@/components/CustomIcon';

export interface IRoute {
  path: string;
  nameKey: string;
  titleKey: string;
  icon?: IconName;
  condition: boolean | ((myInfo?: IMyInfo) => boolean);
  exact?: boolean;
  section?: 'main' | 'digital-school' | 'management';
  subItems?: {
    path: string;
    nameKey: string;
    condition?: boolean | ((myInfo?: IMyInfo) => boolean);
  }[];
}

export const routes: IRoute[] = [
  {
    path: '/parent-operations',
    nameKey: 'parent.name',
    titleKey: 'parent.title',
    icon: 'profile',
    condition: checkIsParent(),
    section: 'main',
  },
  {
    path: '/',
    nameKey: 'home.name',
    titleKey: 'home.title',
    icon: 'dashboard',
    exact: true,
    condition: true,
    section: 'main',
  },
  {
    path: '/lessons',
    nameKey: 'lessons.name',
    titleKey: 'lessons.title',
    icon: 'lessons',
    condition: () => !checkIsRestricted() && !isAZRegion,
    section: 'digital-school',
  },
  {
    path: '/questions',
    nameKey: 'questions.name',
    titleKey: 'questions.title',
    condition: false,
    section: 'digital-school',
  },
  {
    path: '/homeworks',
    nameKey: 'homeworks.name',
    titleKey: 'homeworks.title',
    icon: 'homework',
    condition: true,
    section: 'digital-school',
    subItems: [
      {
        path: '/homeworks?tab=regular',
        nameKey: 'homeworks.sub.regular',
      },
      {
        path: '/homeworks?tab=e12',
        nameKey: 'homeworks.sub.e12',
      },
    ],
  },
  {
    path: '/work-calendar',
    nameKey: 'work-calendar.name',
    titleKey: 'work-calendar.title',
    icon: 'work-calendar',
    condition: () => !checkIsRestricted(),
    section: 'digital-school',
  },
  {
    path: '/timeline',
    nameKey: 'timeline.name',
    titleKey: 'timeline.title',
    icon: 'timeline',
    condition: !isIndividual,
    section: 'digital-school',
  },
  {
    path: '/plans',
    nameKey: 'plans.name',
    titleKey: 'plans.title',
    icon: 'timeline',
    condition: () => !checkIsRestricted() && !isAZRegion,
    section: 'digital-school',
  },
  {
    path: '/live-lessons',
    nameKey: 'live-lessons.name',
    titleKey: 'live-lessons.title',
    icon: 'live-lessons',
    condition: () => !checkIsRestricted(),
    section: 'digital-school',
  },
  {
    path: '/live-exams',
    nameKey: 'live-exams.name',
    titleKey: 'live-exams.title',
    icon: 'live-lessons',
    condition: () => !checkIsRestricted(),
    section: 'digital-school',
  },
  {
    path: '/exam-results',
    nameKey: 'exam-results.name',
    titleKey: 'exam-results.title',
    icon: 'exam-results',
    condition: () => !isIndividual && !isAZRegion,
    section: 'digital-school',
  },
  {
    path: '/etudes',
    nameKey: 'etudes.name',
    titleKey: 'etudes.title',
    icon: 'etudes',
    condition: !isIndividual,
    section: 'digital-school',
  },
  {
    path: '/collective-etudes',
    nameKey: 'collective-etudes.name',
    titleKey: 'collective-etudes.title',
    icon: 'etudes',
    condition: !isIndividual,
    section: 'digital-school',
  },
  {
    path: '/okumanya-app',
    nameKey: 'okumanya-app.name',
    titleKey: 'okumanya-app.title',
    icon: 'lessons',
    condition: (myInfo) => checkOkumanyaAccess(myInfo) && !checkIsRestricted(),
    section: 'digital-school',
  },
  {
    path: '/pdevco-app',
    nameKey: 'pdevco-app.name',
    titleKey: 'pdevco-app.title',
    icon: 'lessons',
    condition: (myInfo) => checkPdevcAccess(myInfo) && !checkIsRestricted(),
    section: 'digital-school',
  },
  {
    path: '/student-tracking-notes',
    nameKey: 'student-tracking-notes.name',
    titleKey: 'student-tracking-notes.title',
    icon: 'note',
    condition: !isIndividual,
    section: 'management',
  },
  {
    path: '/my-attendance',
    nameKey: 'my-attendance.name',
    titleKey: 'my-attendance.title',
    icon: 'attendance',
    condition: !isIndividual,
    section: 'management',
  },
  {
    path: '/academic-calendar',
    nameKey: 'academic-calendar.name',
    titleKey: 'academic-calendar.title',
    icon: 'academic-calendar',
    condition: !isIndividual,
    section: 'management',
  },
  {
    path: '/announcements',
    nameKey: 'announcements.name',
    titleKey: 'announcements.title',
    icon: 'bell',
    condition: true,
    section: 'management',
  },
  {
    path: '/solver',
    nameKey: 'e12-solver.name',
    titleKey: 'e12-solver.title',
    icon: 'solver',
    condition: false,
    section: 'digital-school',
  },
  {
    path: '/organizational-work',
    nameKey: 'organizational-work.name',
    titleKey: 'organizational-work.title',
    icon: 'organizations',
    condition: () => !isIndividual,
    section: 'management',
  },
  {
    path: '/canteen',
    nameKey: 'canteen.name',
    titleKey: 'canteen.title',
    icon: 'meal',
    condition: () => !isIndividual && !checkIsRestricted(),
    section: 'management',
  },
  {
    path: '/profile',
    nameKey: 'profile.name',
    titleKey: 'profile.title',
    condition: false,
  },
  {
    path: '/tahta-app',
    nameKey: 'tahta-app.name',
    titleKey: 'tahta-app.title',
    icon: 'solver',
    condition: (myInfo) => checkTahtaAccess(myInfo) && !checkIsRestricted(),
    section: 'digital-school',
  },
  {
    path: '/messaging',
    nameKey: 'messaging.name',
    titleKey: 'messaging.title',
    icon: 'message',
    condition: true,
    section: 'main',
  },
  {
    path: '/meals',
    nameKey: 'meals.name',
    titleKey: 'meals.title',
    icon: 'meal',
    condition: () => !isIndividual && !checkIsRestricted(),
    section: 'management',
  },
];

export const useTranslatedRoutes = ({
  isParent,
  myInfo,
}: {
  isParent: boolean;
  myInfo?: IMyInfo;
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'routes' });
  const regionConfig = getRegionConfig();
  const filteredRoutes = routes
    .map((route) => {
      if (route.path === '/parent-operations') {
        return {
          ...route,
          condition: isParent,
        };
      }

      return route;
    })
    .filter((route) => {
      if (typeof route.condition === 'function') {
        return route.condition(myInfo);
      }
      return route.condition;
    });

  return filteredRoutes.map((route) => ({
    ...route,
    name: t(route.nameKey, { brand: regionConfig.name }),
    title: t(route.titleKey, { brand: regionConfig.name }),
    subItems: route.subItems?.map((subItem) => ({
      ...subItem,
      name: t(subItem.nameKey, { brand: regionConfig.name }),
    })),
  }));
};
